import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  type ContactFieldAddressInput,
  ContactFieldType,
  type ContactFieldValueInput,
} from '@/api/address-book/types';
import { _matchBusinessCard } from '@/api/contact-import/mutations';
import {
  ComparedField,
  FieldVerdict,
  type MatchBusinessCardPayload,
  type RankedCandidate,
} from '@/api/contact-import/types';
import useDebounce from '@/global-hooks/use-debounce';

import { alreadyPresentValueKeys, isValueHidden } from '../../../import-contacts/email-match';
import type { ContactData } from '../../types';

const MATCH_DEBOUNCE_MS = 600;

const trimmed = (values: string[] | undefined): string[] =>
  (values ?? []).map((value) => value.trim()).filter(Boolean);

export const hasAddressValue = (address: ContactFieldAddressInput): boolean =>
  Boolean(
    address.country?.trim() ||
      address.state?.trim() ||
      address.city?.trim() ||
      address.street?.trim() ||
      address.street2?.trim() ||
      address.postalCode?.trim(),
  );

export function buildCardMatchPayload(data: Partial<ContactData>): MatchBusinessCardPayload {
  const [primaryEmail, ...additionalEmails] = trimmed(data.emails);

  const values: ContactFieldValueInput[] = [
    ...additionalEmails.map((value) => ({ fieldType: ContactFieldType.Email, value })),
    ...trimmed(data.phones).map((value) => ({ fieldType: ContactFieldType.Phone, value })),
    ...trimmed(data.websites).map((value) => ({ fieldType: ContactFieldType.Website, value })),
    ...(data.socialLinks ?? [])
      .filter((link) => link.value.trim())
      .map((link) => ({
        fieldType: ContactFieldType.Social,
        label: link.platform,
        value: link.value.trim(),
      })),
    ...(data.addresses ?? []).filter(hasAddressValue).map((address) => ({
      fieldType: ContactFieldType.Address,
      address: { ...address, street2: address.street2?.trim() || null },
    })),
  ];

  return {
    fullName: data.name?.trim() || null,
    email: primaryEmail ?? null,
    jobTitle: data.jobTitle?.trim() || null,
    company: data.organizationName?.trim() || null,
    values,
  };
}

const isMatchable = (payload: MatchBusinessCardPayload): boolean =>
  Boolean(payload.fullName || payload.email) ||
  (payload.values ?? []).some((v) => v.fieldType === ContactFieldType.Phone);

const normalize = (value: string): string => value.trim().toLowerCase();

function buildIsAlreadyPresent(
  candidate: RankedCandidate | null,
): (_field: string, _value: string) => boolean {
  const presentKeys = alreadyPresentValueKeys(candidate);
  const matchedVerdictValues = new Map<string, Set<string>>();

  for (const verdict of candidate?.fieldVerdicts ?? []) {
    if (verdict.verdict !== FieldVerdict.Matches || !verdict.value) continue;
    const bucket = matchedVerdictValues.get(verdict.field) ?? new Set<string>();
    bucket.add(normalize(verdict.value));
    matchedVerdictValues.set(verdict.field, bucket);
  }

  return (field, value) => {
    if (!value.trim()) return false;
    if (isValueHidden(presentKeys, field, value)) return true;
    return matchedVerdictValues.get(field)?.has(normalize(value)) ?? false;
  };
}

export function useCardMatch(data: Partial<ContactData>) {
  const payload = useMemo(() => buildCardMatchPayload(data), [data]);
  const payloadKey = useDebounce(JSON.stringify(payload), MATCH_DEBOUNCE_MS);
  const enabled = isMatchable(payload);

  const { data: match, isFetching } = useQuery({
    enabled,
    queryKey: ['business-card-match', payloadKey],
    queryFn: () => _matchBusinessCard(JSON.parse(payloadKey) as MatchBusinessCardPayload),
    staleTime: 30_000,
  });

  const topCandidate = match?.candidates?.[0] ?? null;

  return {
    match: match ?? null,
    isMatching: isFetching,
    payload,
    isAlreadyPresent: useMemo(() => buildIsAlreadyPresent(topCandidate), [topCandidate]),
    isNameKnown:
      topCandidate?.fieldVerdicts.find((v) => v.field === ComparedField.Name)?.verdict ===
      FieldVerdict.Matches,
    isAddressKnown:
      topCandidate?.fieldVerdicts.find((v) => v.field === ComparedField.Address)?.verdict ===
      FieldVerdict.Matches,
  };
}
